import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { Minus, Send } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavVisibility } from '../../contexts/NavVisibilityContext';
import { sendNavAssistantMessage } from '../../api/navAssistantApi';
import { useSparks } from '../../contexts/SparksContext';
import AddyAvatar from '../Sparks/AddyAvatar';
import useCosmeticCatalogue from '../Sparks/useCosmeticCatalogue';
import './NavHelpWidget.css';

const MAX_HISTORY = 12;

const SUGGESTED_QUESTIONS = [
  'What do the coloured circles on the Progress Dashboard mean?',
  "What's the difference between Assignments and Quiz Studio?",
  'How does my child log in to do their lessons?',
];

// Replies use **bold** for tab names and nothing else — render just that,
// rather than pulling in a markdown library.
function renderBold(text) {
  return String(text ?? '')
    .split(/(\*\*[^*]+\*\*)/g)
    .map((part, i) =>
      part.startsWith('**') && part.endsWith('**') ? (
        <strong key={i}>{part.slice(2, -2)}</strong>
      ) : (
        <React.Fragment key={i}>{part}</React.Fragment>
      ),
    );
}

export default function NavHelpWidget() {
  const { isAuthenticated, user, loggedInChild } = useAuth();
  // A logged-in child sees their customised Addy; a parent sees the default one.
  const { equipped } = useSparks();
  const cosmeticCatalogue = useCosmeticCatalogue(loggedInChild?._id);
  const { navHidden, askAddyHidden } = useNavVisibility();
  const location = useLocation();
  const token = Cookies.get('token');

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const bottomRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, pending, open]);

  // The component stays mounted while hidden, so state would survive a
  // logout — clear the conversation so the next account never sees it.
  const loggedOut = !isAuthenticated && !token;
  useEffect(() => {
    if (loggedOut) {
      setMessages([]);
      setInput('');
      setOpen(false);
    }
  }, [loggedOut]);

  // Clicking the page outside the panel minimises the chat (keeps messages).
  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  // Same visibility rule as the logged-in nav bar: never float over
  // lessons/quizzes (navHidden) and never for logged-out or inactive users.
  // Also hidden while the child's own Addy is on a working screen (askAddyHidden).
  const visible =
    (isAuthenticated || token) && user?.accessStatus !== 'inactive' && !navHidden && !askAddyHidden;

  // Leave room at the bottom of the page scroll container so last-on-page
  // actions (e.g. Join Lesson) can scroll clear of the fixed launcher.
  useEffect(() => {
    if (!visible) return undefined;
    const scroll = document.getElementById('app-scroll-container');
    if (!scroll) return undefined;
    scroll.classList.add('has-nav-help-widget');
    return () => scroll.classList.remove('has-nav-help-widget');
  }, [visible]);

  if (!visible) return null;

  const send = async (text) => {
    const message = text.trim();
    if (!message || pending) return;

    const outgoing = { role: 'user', content: message };
    // History = the conversation before this message, capped server-side too
    const history = messages
      .filter((m) => !m.isError)
      .slice(-MAX_HISTORY)
      .map(({ role, content }) => ({ role, content }));

    setMessages((prev) => [...prev, outgoing]);
    setInput('');
    setPending(true);
    try {
      const { reply } = await sendNavAssistantMessage({
        message,
        history,
        currentPath: location.pathname,
      });
      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch (err) {
      // Drop the failed question from the thread and hand it back to the input
      setMessages((prev) => [
        ...prev.filter((m) => m !== outgoing),
        {
          role: 'assistant',
          content: "Sorry — I couldn't reply just then. Please try again.",
          isError: true,
        },
      ]);
      setInput(message);
    } finally {
      setPending(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      send(input);
    }
  };

  if (!open) {
    return (
      <button
        className="nav-help-widget__launcher"
        onClick={() => setOpen(true)}
        aria-label="Ask Addy about the website"
      >
        <AddyAvatar equipped={loggedInChild ? equipped : {}} catalogue={cosmeticCatalogue} size={44} className="nav-help-widget__launcher-gif" />
        <span className="nav-help-widget__launcher-label">Ask Addy</span>
      </button>
    );
  }

  return (
    <div
      ref={panelRef}
      className="nav-help-widget__panel"
      role="dialog"
      aria-label="Ask Addy"
    >
      <div className="nav-help-widget__header">
        <AddyAvatar
          equipped={loggedInChild ? equipped : {}}
          catalogue={cosmeticCatalogue}
          size={40}
          mood={pending ? 'talking' : 'sway'}
          className="nav-help-widget__header-gif"
        />
        <div className="nav-help-widget__header-title">
          <span>Addy</span>
          <small>Nova Learning helper</small>
        </div>
        <button
          className="nav-help-widget__close"
          onClick={() => setOpen(false)}
          aria-label="Minimise Ask Addy"
          title="Minimise"
        >
          <Minus size={16} />
        </button>
      </div>

      <div className="nav-help-widget__messages">
        <div className="nav-help-widget__bubble nav-help-widget__bubble--addy">
          Hi! I'm Addy 👋 Ask me anything about how Nova Learning works.
        </div>
        {messages.length === 0 && (
          <div className="nav-help-widget__suggestions">
            {SUGGESTED_QUESTIONS.map((q) => (
              <button
                key={q}
                className="nav-help-widget__suggestion"
                onClick={() => send(q)}
                disabled={pending}
              >
                {q}
              </button>
            ))}
          </div>
        )}
        {messages.map((msg, index) => (
          <div
            key={index}
            className={`nav-help-widget__bubble ${
              msg.role === 'user'
                ? 'nav-help-widget__bubble--user'
                : 'nav-help-widget__bubble--addy'
            }`}
          >
            {renderBold(msg.content)}
          </div>
        ))}
        {pending && (
          <div className="nav-help-widget__bubble nav-help-widget__bubble--addy nav-help-widget__bubble--typing">
            Addy is typing…
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="nav-help-widget__input-row">
        <textarea
          className="nav-help-widget__input"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about the website..."
          maxLength={500}
          rows={1}
          disabled={pending}
        />
        <button
          className="nav-help-widget__send"
          onClick={() => send(input)}
          disabled={!input.trim() || pending}
          aria-label="Send question"
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );
}
